/**
 * useAnchorEntities — loads the four sub-anchor lists (parts, batches, bank
 * guarantees, inspection events) for the AnchorPicker, given a projectId.
 *
 * Background: the AnchorPicker disables a sub-anchor radio when its data
 * array is empty (e.g. `disabled={batches.length === 0}`). Callers
 * previously had to gather these themselves, and both call sites
 * (project-detail.tsx, project-documents-tab.tsx) used to hardcode
 * `batches: []` and `inspectionEvents: []` with a "populated lazily" TODO.
 * That left the Batch and Inspection radios permanently disabled and the
 * Communications page picker entirely non-functional because it has no
 * projectId until the user picks one in the modal.
 *
 * This hook centralises the data plumbing: pass it a `projectId | null`
 * and it returns the four arrays plus a loading flag. Used by
 * LogCommunicationModal so every call site gets correct entities without
 * the caller doing per-part fan-out gymnastics.
 *
 * Batches and inspection events live on whichever item carries the stage —
 * for Variant A parts that's the SUB_PART, for Variant B it's the PART. We
 * fan out across ALL items, not just `itemType === 'PART'`, so both
 * variants work.
 */
import { useQueries, useQuery } from '@tanstack/react-query';
import { formatCurrency, formatDate } from '@procurement/shared';
import { apiRequest } from '@/lib/api-client';
import type {
  InspectionEvent,
  OrderBatch,
  ProjectItem,
  BankGuarantee,
} from '@/lib/pipeline-queries';
import type { EntityOption } from '@/components/attachments/anchor-picker';

export interface AnchorEntitiesResult {
  parts: EntityOption[];
  batches: EntityOption[];
  bankGuarantees: EntityOption[];
  inspectionEvents: EntityOption[];
  isLoading: boolean;
}

export function useAnchorEntities(projectId: string | null): AnchorEntitiesResult {
  // Inline `useQuery` calls (not `useProjectItems` / `useBankGuarantees`)
  // so we can gate them with `enabled`. Without that, the hooks would fire
  // a request to `/api/projects//items` when projectId is null.
  const items = useQuery({
    queryKey: ['projects', projectId, 'items'] as const,
    queryFn: ({ signal }) =>
      apiRequest<ProjectItem[]>(`/api/projects/${projectId}/items`, { signal }),
    enabled: Boolean(projectId),
  });
  const bgs = useQuery({
    queryKey: ['projects', projectId, 'bank-guarantees'] as const,
    queryFn: ({ signal }) =>
      apiRequest<BankGuarantee[]>(`/api/projects/${projectId}/bank-guarantees`, { signal }),
    enabled: Boolean(projectId),
  });

  const allItemIds = projectId ? (items.data ?? []).map((it) => it.id) : [];

  const batchQueries = useQueries({
    queries: allItemIds.map((itemId) => ({
      queryKey: ['projects', projectId, 'items', itemId, 'batches'] as const,
      queryFn: ({ signal }: { signal?: AbortSignal }) =>
        apiRequest<OrderBatch[]>(`/api/projects/${projectId}/items/${itemId}/batches`, {
          signal,
        }),
      enabled: Boolean(projectId && itemId),
    })),
  });

  const inspectionQueries = useQueries({
    queries: allItemIds.map((itemId) => ({
      queryKey: ['projects', projectId, 'items', itemId, 'inspection-events'] as const,
      queryFn: ({ signal }: { signal?: AbortSignal }) =>
        apiRequest<InspectionEvent[]>(
          `/api/projects/${projectId}/items/${itemId}/inspection-events`,
          { signal },
        ),
      enabled: Boolean(projectId && itemId),
    })),
  });

  if (!projectId) {
    return {
      parts: [],
      batches: [],
      bankGuarantees: [],
      inspectionEvents: [],
      isLoading: false,
    };
  }

  // partLabel lookup: SUB_PARTs label themselves with their parent's Sr
  // number plus their own name (e.g. "Sr 3 / inner bushing"), so the batch
  // / inspection rows that hang off them are recognisable. Top-level PARTs
  // use the standard "Sr N" prefix.
  const partLabelById = new Map<string, string>();
  for (const it of items.data ?? []) {
    if (it.itemType === 'PART') {
      partLabelById.set(it.id, it.srNumber != null ? `Sr ${it.srNumber}` : it.name.slice(0, 24));
    }
  }
  for (const it of items.data ?? []) {
    if (it.itemType === 'SUB_PART' && it.parentItemId) {
      const parentLabel = partLabelById.get(it.parentItemId) ?? '—';
      partLabelById.set(it.id, `${parentLabel} / ${it.name.slice(0, 18)}`);
    }
  }

  const parts: EntityOption[] = (items.data ?? [])
    .filter((it) => it.itemType === 'PART')
    .map((it) => ({
      id: it.id,
      label: it.srNumber != null ? `Sr ${it.srNumber} — ${it.name}` : it.name,
    }));

  const batches: EntityOption[] = batchQueries
    .flatMap((q) => q.data ?? [])
    .map((b) => {
      const partLabel = partLabelById.get(b.projectItemId) ?? '—';
      return {
        id: b.id,
        label: `${partLabel} · ${b.supplierNameSnapshot} (qty ${b.quantity})`,
      };
    });

  const bankGuarantees: EntityOption[] = (bgs.data ?? []).map((bg) => {
    const amount = formatCurrency(bg.amountPaisa, { compact: true });
    const dateBit = bg.submittedDate ? ` · ${formatDate(bg.submittedDate)}` : '';
    return { id: bg.id, label: `BG ${amount}${dateBit}` };
  });

  const inspectionEvents: EntityOption[] = inspectionQueries
    .flatMap((q) => q.data ?? [])
    .map((i) => {
      const partLabel = partLabelById.get(i.projectItemId) ?? '—';
      return {
        id: i.id,
        label: `${partLabel} · stage ${i.stageNumber} round ${i.eventRound} (${formatDate(i.eventDate)})`,
      };
    });

  return {
    parts,
    batches,
    bankGuarantees,
    inspectionEvents,
    isLoading:
      items.isLoading ||
      bgs.isLoading ||
      batchQueries.some((q) => q.isLoading) ||
      inspectionQueries.some((q) => q.isLoading),
  };
}
